import type { FormInstance } from "@ir/ui";
import {
  CreateMasterDataDeductionTypeDTO,
  UpdateMasterDataDeductionTypeDTO,
} from "@src/domain/dto/masterData";
import type { MasterDataDeductionType } from "@src/domain/entities/masterData";
import type { DeductionTypeAddEditFormValues } from "./MasterDataDeductionTypeAddEditForm";
import {
  DEDUCTION_TYPE_KEY_ALREADY_EXISTS_MESSAGE,
  isDeductionTypeKeyDuplicateError,
} from "./deductionTypeKeyValidation";
import { getMasterDataSaveErrorMessage } from "./masterDataSaveErrors";
import type { MasterDataAddEditModalMode } from "./useMasterDataAddEditModal";

const EMPTY_DEDUCTION_TYPE_FORM_VALUES: DeductionTypeAddEditFormValues =
  {
    name: "",
    deductionTypeKey: "",
    deductionTypeCategoryId: null,
    helpText: "",
    metadata: "",
  };

const DEDUCTION_TYPE_LINKED_DELETE_WARNING =
  "Cannot delete deduction type assigned to deductions.";

type ToastContentFn = (args: { content: string }) => void;

function stringifyDeductionTypeMetadata(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "";
  }
}

function parseDeductionTypeMetadata(value: string): unknown {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return trimmed;
  }
}

export function getDeductionTypeFormInitialValues(
  modalMode: MasterDataAddEditModalMode | null,
  editingEntity: MasterDataDeductionType | null,
): DeductionTypeAddEditFormValues {
  if (modalMode === "edit" && editingEntity) {
    return {
      name: editingEntity.name,
      deductionTypeKey: editingEntity.deductionTypeKey,
      deductionTypeCategoryId: editingEntity.categoryId,
      helpText: editingEntity.helpText ?? "",
      metadata: stringifyDeductionTypeMetadata(editingEntity.metadata),
    };
  }
  return EMPTY_DEDUCTION_TYPE_FORM_VALUES;
}

function buildDeductionTypeSavePayload(
  values: DeductionTypeAddEditFormValues,
  options: {
    modalMode: MasterDataAddEditModalMode | null;
    editingEntity: MasterDataDeductionType | null;
    parentId?: number | null;
  },
) {
  const name = values.name.trim();
  const deductionTypeKey =
    options.modalMode === "edit" && options.editingEntity
      ? options.editingEntity.deductionTypeKey
      : values.deductionTypeKey.trim();

  let helpText: string | null = null;
  if (options.parentId != null) {
    helpText = values.helpText.trim() || null;
  } else if (options.modalMode === "edit" && options.editingEntity) {
    helpText = options.editingEntity.helpText;
  }

  return {
    name,
    deductionTypeKey,
    deductionTypeCategoryId: values.deductionTypeCategoryId ?? null,
    ...(options.parentId != null ? { parentId: options.parentId } : {}),
    helpText,
    metadata: parseDeductionTypeMetadata(values.metadata ?? ""),
  };
}

export async function saveMasterDataDeductionType(options: {
  values: DeductionTypeAddEditFormValues;
  modalMode: MasterDataAddEditModalMode | null;
  editingEntity: MasterDataDeductionType | null;
  parentId?: number | null;
  createUseCase: { execute: (dto: CreateMasterDataDeductionTypeDTO) => Promise<unknown> };
  updateUseCase: { execute: (dto: UpdateMasterDataDeductionTypeDTO) => Promise<unknown> };
}): Promise<string> {
  const payload = buildDeductionTypeSavePayload(options.values, {
    modalMode: options.modalMode,
    editingEntity: options.editingEntity,
    parentId: options.parentId,
  });

  if (options.modalMode === "edit" && options.editingEntity) {
    await options.updateUseCase.execute(
      new UpdateMasterDataDeductionTypeDTO({
        typeId: options.editingEntity.typeId,
        ...payload,
      }),
    );
  } else {
    await options.createUseCase.execute(
      new CreateMasterDataDeductionTypeDTO(payload),
    );
  }

  return payload.name;
}

export function applyDeductionTypeSaveError(
  err: unknown,
  form: FormInstance<DeductionTypeAddEditFormValues>,
  setIsFormSubmittable: (isSubmittable: boolean) => void,
  errorToast: ToastContentFn,
  fallbackMessage: string,
  networkMessage: string,
): void {
  if (isDeductionTypeKeyDuplicateError(err)) {
    form.setFields([
      {
        name: "deductionTypeKey",
        errors: [DEDUCTION_TYPE_KEY_ALREADY_EXISTS_MESSAGE],
      },
    ]);
    setIsFormSubmittable(false);
    return;
  }
  errorToast({
    content: getMasterDataSaveErrorMessage(
      err,
      fallbackMessage,
      networkMessage,
    ),
  });
}

export function notifyDeductionTypeDeleteResult(
  result: { deletedCount: number; blockedCount: number },
  labels: { singular: string; plural: string },
  successToast: ToastContentFn,
  errorToast: ToastContentFn,
): void {
  if (result.deletedCount > 0) {
    successToast({
      content: `Deleted ${result.deletedCount} ${
        result.deletedCount === 1 ? labels.singular : labels.plural
      }.`,
    });
  }
  if (result.blockedCount > 0) {
    errorToast({ content: DEDUCTION_TYPE_LINKED_DELETE_WARNING });
  }
}

export function getMasterDataDeleteErrorMessage(
  err: unknown,
  fallbackMessage: string,
): string {
  return err instanceof Error && err.message.trim()
    ? err.message
    : fallbackMessage;
}
